export default function Services() {
  return (
    <>
      {/* The Problem Section */}
      <section className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Diabetic retinopathy is common, and screening is consistently missed.
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            Even when patients see their primary care provider, specialty eye referrals often don't happen due to access constraints, transportation, scheduling friction, and limited awareness. The result is avoidable vision loss and persistent quality gaps.
          </p>
        </div>
      </section>

      {/* The Kinra Solution - 3 Pillars */}
      <section id="solution" className="py-24 px-6 lg:px-12 bg-[#FAFAF9]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm mb-6">
              <span className="w-2 h-2 bg-primary rounded-full animate-pulse"></span>
              <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">The Kinra Solution</span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              A full-stack program, not just a device.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Pillar 1 */}
            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all">
              <div className="w-16 h-16 bg-primary/20 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-hospital-line text-primary text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Embedded screening at the point of care
              </h3>
              <p className="text-gray-600 leading-relaxed">
                We place devices and trained professionals inside clinics and community sites to make screening a routine step, not a referral.
              </p>
            </div>

            {/* Pillar 2 */}
            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all">
              <div className="w-16 h-16 bg-primary/20 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-truck-line text-primary text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Mobile dispatch for hard-to-reach populations
              </h3>
              <p className="text-gray-600 leading-relaxed">
                When patients can't reliably come to care—or care can't reliably reach them—Kinra deploys mobile teams to deliver screening where it's most effective.
              </p>
            </div>

            {/* Pillar 3 */}
            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all">
              <div className="w-16 h-16 bg-primary/20 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-dashboard-line text-primary text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Software that makes it measurable
              </h3>
              <p className="text-gray-600 leading-relaxed">
                We support intake, workflow prompts, results delivery, documentation, and reporting so screenings translate into closed gaps and measurable quality performance.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Outcomes Section */}
      <section className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Designed to improve quality measures and performance payments.
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our approach delivers measurable improvements across the quality indicators that matter most to your organization.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Outcome 1 */}
            <div className="group relative bg-gradient-to-br from-[#FAFAF9] to-white rounded-2xl p-10 shadow-sm hover:shadow-2xl transition-all border border-gray-100">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary to-primary-light rounded-t-2xl"></div>
              <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-arrow-up-line text-white text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Higher Screening Rates
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Dramatically increase diabetic eye exam completion rates by removing access barriers and meeting patients where they are.
              </p>
            </div>

            {/* Outcome 2 */}
            <div className="group relative bg-gradient-to-br from-[#FAFAF9] to-white rounded-2xl p-10 shadow-sm hover:shadow-2xl transition-all border border-gray-100">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary to-primary-light rounded-t-2xl"></div>
              <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-star-line text-white text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Improved Quality Scores
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Boost performance on Stars and HEDIS-aligned measures with audit-ready documentation and comprehensive reporting.
              </p>
            </div>

            {/* Outcome 3 */}
            <div className="group relative bg-gradient-to-br from-[#FAFAF9] to-white rounded-2xl p-10 shadow-sm hover:shadow-2xl transition-all border border-gray-100">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary to-primary-light rounded-t-2xl"></div>
              <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-eye-line text-white text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Better Gap Visibility
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Gain real-time insights into care gaps with member-level tracking and targeted outreach capabilities.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
